import { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, DialogActions, DialogContent, LinearProgress, TextField, Typography,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DraggableDialog from '../../components/DraggableDialog';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import { creativesService } from '../../services/creatives.service';
import { CREATIVE_ACCEPT, formatFileSize } from './creative-format';

export default function UploadCreativeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [workspaceId, setWorkspaceId] = useState('');
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: () => creativesService.upload(file!, {
      workspaceId, name: name.trim() || undefined, tags, onProgress: setProgress,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creatives'] });
      queryClient.invalidateQueries({ queryKey: ['creatives-library'] });
      onClose();
    },
  });

  const resetUpload = upload.reset;
  useEffect(() => {
    if (!open) return;
    setFile(null); setName(''); setTags([]); setProgress(0); setRejected(null);
    setWorkspaceId(defaultCreateWorkspace());
    resetUpload();
  }, [open, resetUpload]);

  const pick = (f: File | undefined) => {
    if (!f || upload.isPending) return;
    // A drop skips the chooser's accept filter, and the large-file path doesn't check the type server-side.
    if (!CREATIVE_ACCEPT.split(',').includes(f.type)) { setRejected(`${f.name}: unsupported file type`); return; }
    setRejected(null);
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^/.]+$/, ''));
  };

  const busy = upload.isPending;
  const error = upload.error as { response?: { data?: { error?: { message?: string } } }; message?: string } | null;

  return (
    <DraggableDialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth title="Upload Creative" titleId="upload-creative-title">
      <DialogContent>
        <Box
          role="button"
          tabIndex={0}
          aria-label="Choose a file"
          onClick={() => !busy && inputRef.current?.click()}
          onKeyDown={(e) => { if (!busy && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
          sx={{
            border: '2px dashed', borderColor: dragging ? 'primary.main' : 'divider', borderRadius: 2,
            p: 3, mb: 2, textAlign: 'center', cursor: busy ? 'default' : 'pointer',
            bgcolor: dragging ? 'action.hover' : 'transparent',
          }}
        >
          <CloudUpload sx={{ fontSize: 40, color: 'text.secondary' }} />
          {file ? (
            <Typography variant="body2" sx={{ mt: 1 }}>{file.name} ({formatFileSize(file.size)})</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Drop an image, video or audio file here, or click to choose
            </Typography>
          )}
        </Box>
        <input ref={inputRef} type="file" accept={CREATIVE_ACCEPT} hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Name" size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
          <WorkspaceField value={workspaceId} onChange={setWorkspaceId} disabled={busy} />
          <Autocomplete
            multiple freeSolo size="small" options={[]} value={tags} disabled={busy}
            // Tags travel comma-joined, so a comma inside one would split it on the server anyway.
            onChange={(_, v) => setTags([...new Set((v as string[]).flatMap((t) => t.split(',')).map((t) => t.trim()).filter(Boolean))])}
            renderTags={(value, getTagProps) => value.map((t, i) => <Chip size="small" label={t} {...getTagProps({ index: i })} key={t} />)}
            renderInput={(p) => <TextField {...p} label="Tags" placeholder="Type and press Enter" />}
          />
        </Box>

        {rejected && <Alert severity="warning" sx={{ mt: 2 }}>{rejected}</Alert>}
        {busy && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error.response?.data?.error?.message || error.message || 'Upload failed'}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained" startIcon={<CloudUpload />}
          disabled={!file || !workspaceId || busy}
          onClick={() => upload.mutate()}
        >
          {busy ? 'Uploading…' : 'Upload'}
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
}
